import { useRef, useState, useEffect, useMemo } from 'react';
import { ArrowRight } from 'react-feather';
import { useNavigate } from 'react-router';
import {
	AgentModeToggle,
	type AgentMode,
} from '../components/agent-mode-toggle';
import { useAuthGuard } from '../hooks/useAuthGuard';

export default function Home() {
	const navigate = useNavigate();
	const { requireAuth } = useAuthGuard();
	const textareaRef = useRef<HTMLTextAreaElement>(null);
	const [agentMode, setAgentMode] = useState<AgentMode>('deterministic');
	
	
	const placeholderPhrases = useMemo(() => [
		"todo list app",
		"F1 fantasy game",
		"personal finance tracker"
	], []);
	const [currentPlaceholderPhraseIndex, setCurrentPlaceholderPhraseIndex] = useState(0);
	const [currentPlaceholderText, setCurrentPlaceholderText] = useState("");
	const [isPlaceholderTyping, setIsPlaceholderTyping] = useState(true);

	const handleCreateApp = (query: string, mode: AgentMode) => {
		const encodedQuery = encodeURIComponent(query);
		const encodedMode = encodeURIComponent(mode);
		const intendedUrl = `/chat/new?query=${encodedQuery}&agentMode=${encodedMode}`;

		if (
			!requireAuth({
				requireFullAuth: true,
				actionContext: 'to create applications',
				intendedUrl: intendedUrl,
			})
		) {
			return;
		}

		// User is already authenticated, navigate immediately
		navigate(intendedUrl);
	};

	// Auto-resize textarea based on content
	const adjustTextareaHeight = () => {
		if (textareaRef.current) {
			textareaRef.current.style.height = 'auto';
			const scrollHeight = textareaRef.current.scrollHeight;
			const maxHeight = 300; // Maximum height in pixels
			textareaRef.current.style.height =
				Math.min(scrollHeight, maxHeight) + 'px';
		}
	};

	useEffect(() => {
		adjustTextareaHeight();
	}, []);
	
	// Typewriter effect
	useEffect(() => {
		const currentPhrase = placeholderPhrases[currentPlaceholderPhraseIndex];
		
		if (isPlaceholderTyping) {
			if (currentPlaceholderText.length < currentPhrase.length) {
				const timeout = setTimeout(() => {
					setCurrentPlaceholderText(currentPhrase.slice(0, currentPlaceholderText.length + 1));
				}, 100); // Typing speed
				return () => clearTimeout(timeout);
			} else {
				// Pause before erasing
				const timeout = setTimeout(() => {
					setIsPlaceholderTyping(false);
				}, 2000); // Pause duration
				return () => clearTimeout(timeout);
			}
		} else {
			if (currentPlaceholderText.length > 0) {
				const timeout = setTimeout(() => {
					setCurrentPlaceholderText(currentPlaceholderText.slice(0, -1));
				}, 50); // Erasing speed
				return () => clearTimeout(timeout);
			} else {
				// Move to next phrase
				setCurrentPlaceholderPhraseIndex((prev) => (prev + 1) % placeholderPhrases.length);
				setIsPlaceholderTyping(true);
			}
		}
	}, [currentPlaceholderText, currentPlaceholderPhraseIndex, isPlaceholderTyping, placeholderPhrases]);
	return (
		<div className="min-h-screen bg-gradient-to-br from-[rgb(31,118,209)]/10 via-purple-50 to-pink-50 dark:from-gray-900 dark:via-purple-900 dark:to-pink-900">
			{/* AppMachine-style wave background */}
			<div className="absolute inset-0 overflow-hidden">
				<div className="absolute -top-40 -right-40 w-80 h-80 bg-gradient-to-br from-[rgb(31,118,209)] to-purple-500 rounded-full opacity-20 blur-3xl"></div>
				<div className="absolute -bottom-40 -left-40 w-80 h-80 bg-gradient-to-br from-pink-400 to-orange-500 rounded-full opacity-20 blur-3xl"></div>
			</div>

			<div className="relative flex flex-col items-center justify-center min-h-screen px-4">
				<div className="w-full max-w-4xl text-center mb-12">
					<h1 className="text-5xl md:text-7xl font-bold mb-6 text-[rgb(31,118,209)] leading-tight">
						What should we build today? 🚀
					</h1>
					<p className="text-xl md:text-2xl text-gray-600 dark:text-gray-300 mb-8 max-w-2xl mx-auto">
						Describe your app idea and watch it come to life with AI-powered development
					</p>
				</div>

				<div className="w-full max-w-2xl">
					<form
						method="POST"
						onSubmit={(e) => {
							e.preventDefault();
							const query = textareaRef.current!.value;
							handleCreateApp(query, agentMode);
						}}
						className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-xl border border-white/20 dark:border-gray-700/50 rounded-3xl p-8 shadow-2xl shadow-purple-500/10"
					>
						<div className="mb-6">
							<textarea
								className="w-full h-32 resize-none border-0 bg-transparent text-lg placeholder:text-gray-500 dark:placeholder:text-gray-400 text-gray-900 dark:text-white focus:outline-none"
								name="query"
								placeholder={`Create a ${currentPlaceholderText}...`}
								ref={textareaRef}
								onChange={adjustTextareaHeight}
								onInput={adjustTextareaHeight}
								onKeyDown={(e) => {
									if (e.key === 'Enter' && !e.shiftKey) {
										e.preventDefault();
										const query = textareaRef.current!.value;
										handleCreateApp(query, agentMode);
									}
								}}
							/>
						</div>

						<div className="flex flex-col md:flex-row items-center justify-between gap-4">
							{import.meta.env.VITE_AGENT_MODE_ENABLED ? (
								<AgentModeToggle
									value={agentMode}
									onChange={setAgentMode}
									className="w-full md:w-auto"
								/>
							) : (
								<div></div>
							)}

							<button
								type="submit"
								className="bg-[rgb(31,118,209)] hover:bg-[rgb(31,118,209)]/90 text-white font-semibold px-8 py-4 rounded-2xl transition-all duration-200 hover:shadow-lg hover:shadow-[rgb(31,118,209)]/25 hover:scale-105 flex items-center gap-2 w-full md:w-auto justify-center"
							>
								Build My App
								<ArrowRight className="w-5 h-5" />
							</button>
						</div>
					</form>

					{/* Feature highlights */}
					<div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6 text-center">
						<div className="p-6 bg-white/60 dark:bg-gray-800/60 backdrop-blur-lg rounded-2xl border border-white/20 dark:border-gray-700/50">
							<div className="text-3xl mb-3">⚡</div>
							<h3 className="font-semibold text-gray-900 dark:text-white mb-2">Lightning Fast</h3>
							<p className="text-sm text-gray-600 dark:text-gray-400">AI-powered development in minutes, not months</p>
						</div>
						<div className="p-6 bg-white/60 dark:bg-gray-800/60 backdrop-blur-lg rounded-2xl border border-white/20 dark:border-gray-700/50">
							<div className="text-3xl mb-3">🎨</div>
							<h3 className="font-semibold text-gray-900 dark:text-white mb-2">Beautiful Design</h3>
							<p className="text-sm text-gray-600 dark:text-gray-400">Modern, responsive interfaces that users love</p>
						</div>
						<div className="p-6 bg-white/60 dark:bg-gray-800/60 backdrop-blur-lg rounded-2xl border border-white/20 dark:border-gray-700/50">
							<div className="text-3xl mb-3">🔧</div>
							<h3 className="font-semibold text-gray-900 dark:text-white mb-2">Full Stack</h3>
							<p className="text-sm text-gray-600 dark:text-gray-400">Complete applications with frontend and backend</p>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
}
